import type { ComponentType } from 'react';
import { Route } from 'react-router-dom';

interface LocalizedRoutesOptions<L extends string, P extends string> {
  locales: readonly L[];
  pages: readonly P[];
  /** One component per page; a Record type, so adding a page without one fails to compile. */
  components: Record<P, ComponentType>;
  /** The page's full URL path in a locale, e.g. localizePath or msPath. */
  pathFor: (locale: L, page: P) => string;
  /** The section's base ('/travo', '/membersheep'), whose `/*` route these routes sit under. */
  base: string;
}

/**
 * A section's <Route> for every page in every locale. Routes inside a section are matched
 * relative to the app's `${base}/*` route, so each path drops the base: '/travo/zh-tw' → '/zh-tw'.
 */
export const localizedRoutes = <L extends string, P extends string>({
  locales,
  pages,
  components,
  pathFor,
  base
}: LocalizedRoutesOptions<L, P>) =>
  locales.flatMap((locale) =>
    pages.map((page) => {
      const Page: ComponentType = components[page];
      const path = pathFor(locale, page).slice(base.length) || '/';
      return <Route key={path} path={path} element={<Page />} />;
    })
  );
